import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { GridSmartViewsWebComponent } from '../component/GridSmartViewsWebComponent';
import { GridSmartViewsView } from '../view/GridSmartViewsView';
import { templateGridSmartViews } from '../view/template';
import {
  GridSmartViewsWebComponentSchema,
  SectionType,
  SmartViewEvents,
  SmartViewInlineActions,
  SmartViewItemConfiguration,
} from '../types';
import { ButtonColors, ButtonTypes } from '../../../ui/gzButton/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzListItemWebComponent } from '../../../ui/gzListItem';
import { ListItemEvents } from '../../../ui/gzListItem/types';
import { GridSaveViewForm } from '../../uiElements/smartViewSaveDialog';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { SaveViewFormDialogSchema } from '../../uiElements/smartViewSaveDialog/types';
import { htmlEncode } from '../../../utils/escape.utils';

export class GridSmartViewsController<Column, Filter, Type> extends BaseWebComponentController {
  public component: GridSmartViewsWebComponent<Column, Filter, Type>;
  public view: GridSmartViewsView;
  public viewConstructor: typeof GridSmartViewsView;

  constructor(component: GridSmartViewsWebComponent<Column, Filter, Type>) {
    super(component);
    this.initialSetup({
      viewConstructor: GridSmartViewsView,
      viewTemplate: templateGridSmartViews,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$sectionsDiv,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleViewItemClicked,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: SmartViewInlineActions.RENAME,
        callback: this.showViewRenameInput,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: SmartViewInlineActions.DELETE,
        callback: this.showDeleteDialog,
      },
      {
        element: this.view.dom.$sectionsDiv,
        event: ListItemEvents.SAVE,
        callback: this.handleViewNameUpdated,
      },
    ];
  }

  async getDataForView(): Promise<GridSmartViewsWebComponentSchema<Column, Filter, Type>> {
    return {
      sections: this.component.sections,
      activeViewId: this.component.activeViewId,
    };
  }

  protected handleViewItemClicked(event: WebComponentEvent) {
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SELECTED, {
        detail: event.detail,
        bubbles: true,
      })
    );
  }

  private handleViewNameUpdated(event: WebComponentEvent) {
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_NAME_UPDATED, {
        detail: {
          updatedViewName: event.detail.value.trimLeft().trimRight(),
          smartViewItemConfiguration: event.detail.itemData,
        },
        bubbles: true,
      })
    );
  }

  private showDeleteDialog(event: WebComponentEvent) {
    const translations = this.view.getTranslations();
    const modalString = this.dependencies.translateFn('Are you sure you want to delete <strong>{viewName}</strong> ?', {
      viewName: htmlEncode(event.detail.itemData.viewName),
    });

    const modalBodyString = `<p> ${modalString} </p>`;

    const footerString = `<gz-button type="${ButtonTypes.OUTLINE}" color="${
      ButtonColors.NEGATIVE
    }" id="confirm"> ${translations.actions.delete.toUpperCase()} </gz-button>
                          <gz-button type="${ButtonTypes.OUTLINE}" color="${
      ButtonColors.SECONDARY
    }" id="cancel"> ${translations.actions.cancel.toUpperCase()} </gz-button>
`;
    const cancelCallback = null;
    const confirmCallback = () => {
      this.component.dispatchEvent(
        new CustomEvent(SmartViewEvents.SMART_VIEW_DELETED, {
          detail: {
            viewId: event.detail.itemData.id,
            viewName: event.detail.itemData.viewName,
          },
          bubbles: true,
          composed: true,
        })
      );
    };

    GzDialogWebComponent.computeCustomDialog(
      modalBodyString,
      footerString,
      translations.deleteViewFormDialog.title,
      cancelCallback,
      confirmCallback
    );
  }

  public showSaveAsDialog(config: SaveViewFormDialogSchema) {
    const saveForm = new GridSaveViewForm();

    if (config.shouldDisplayCheckbox) {
      saveForm.shouldDisplayCheckbox = config.shouldDisplayCheckbox;
      saveForm.checkboxLabel = config.checkboxLabel;
    }

    saveForm.saveButtonLabel = config.saveButtonLabel;
    saveForm.cancelButtonLabel = config.cancelButtonLabel;
    saveForm.dialogText = config.dialogText;
    saveForm.nameFieldLabel = config.nameFieldLabel;
    saveForm.nameInputErrorText = config.nameInputErrorText;
    saveForm.nameInputPlaceholder = config.nameInputPlaceholder;
    saveForm.nameInputValidators = config.nameInputValidators;
    saveForm.inputMaxLength = 64;

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: config.headline,
      bodyContent: saveForm,
      isOpen: true,
    });

    saveForm.addEventListener(GzDialogEvents.CANCEL, event => {
      dialog.close();
    });

    saveForm.addEventListener(GzDialogEvents.CONFIRM, (event: WebComponentEvent) => {
      this.component.dispatchEvent(
        new CustomEvent(SmartViewEvents.SMART_VIEW_CREATED, {
          detail: {
            viewName: event.detail.viewName,
            isShared: event.detail.isShared,
          },
          bubbles: true,
        })
      );
      dialog.close();
    });
  }

  private showViewRenameInput(event: WebComponentEvent) {
    const target = event.target as GzListItemWebComponent;
    target.editable = true;
  }

  public async reloadSections(): Promise<void> {
    this.view.dom.$sectionsDiv.innerHTML = '';
    await this.render();
  }

  /**
   * Removes selected view from a section
   *
   * @param {string} viewId
   * @returns {Promise<void>}
   */
  public async removeViewFromSection(viewId: string): Promise<void> {
    const currentSections = this.component.sections;

    for (const section of currentSections) {
      section.items = section.items.filter(view => view.id !== viewId);
    }

    await this.reloadSections();
  }

  /**
   * Adds a view to a specific section
   *
   * @param {SmartViewItemConfiguration} smartViewItemConfiguration
   * @param {SectionType} sectionType
   * @returns {Promise<void>}
   */
  public async addViewToSection(
    smartViewItemConfiguration: SmartViewItemConfiguration<Column, Filter, Type>,
    sectionType: SectionType
  ): Promise<void> {
    if (sectionType === SectionType.DEFAULT) {
      throw new Error('Cannot add default views');
    }

    const currentSections = this.component.sections;

    for (const section of currentSections) {
      if (section.type !== sectionType) {
        continue;
      }
      section.items.push(smartViewItemConfiguration);
    }

    await this.reloadSections();
  }

  /**
   * Replaces view properties of a single entry from section items based on the viewId of the configuration
   *
   * @param {SmartViewItemConfiguration} smartViewItemConfiguration
   * @param {SectionType} sectionType
   * @param shouldSetAsActiveView
   * @returns {Promise<void>}
   */
  public async updateViewPropsFromSection(
    smartViewItemConfiguration: SmartViewItemConfiguration<Column, Filter, Type>,
    sectionType: SectionType,
    shouldSetAsActiveView: boolean = false
  ) {
    if (sectionType === SectionType.DEFAULT) {
      throw new Error('Cannot add default views');
    }

    const currentSections = this.component.sections;

    for (const section of currentSections) {
      if (section.type !== sectionType) {
        continue;
      }

      section.items = section.items.map((viewConfiguration: SmartViewItemConfiguration<Column, Filter, Type>) => {
        if (viewConfiguration.id !== smartViewItemConfiguration.id) {
          return viewConfiguration;
        }
        return smartViewItemConfiguration;
      });
    }

    if (shouldSetAsActiveView) {
      this.component.activeViewId = smartViewItemConfiguration.id;
    }

    await this.reloadSections();
  }
}
